import { EventEmitter, Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import {
  DISABLE_ANALYTICS_COOKIES_COOKIE_KEY,
  DISABLE_ETRACKER_COOKIE_KEY,
  DISABLE_GOOGLE_TAG_MANAGER_COOKIE_KEY,
  DISABLE_TARGETING_COOKIES_COOKIE_KEY,
  DISABLE_APPSFLYER_COOKIE_KEY
} from "app/common";
import { BehaviorSubject } from "rxjs";
import { DateHelper } from "app/core/helpers/date.helper";
import { FacebookPixelConsentService } from "app/shared/services/facebook-pixel/facebook-pixel-consent.service";
import { EtrackerConsentService } from "app/shared/services/etracker/e-tracker-consent.service";
import { AppsFlyerConsentService } from "app/shared/services/appsflyer/appsflyer-consent.service";

@Injectable()
export class CookiesLawService {
  public changesEmitter = new EventEmitter();
  public isBannerRequired: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private readonly analyticsCookiesKeysList = ["_ga", "_gid", "_dd_s"];
  // "disable-targeting-cookies" is not in use now. May need to be removed.
  private readonly targetingCookiesKeysList = ["_gcl_au", "_fbp"];

  constructor(
    private cookieService: CookieService,
    private facebookConsent: FacebookPixelConsentService,
    private etrackerConsent: EtrackerConsentService,
    private appsflyerConsent: AppsFlyerConsentService
  ) {
    setTimeout(() => {
      this.checkIfBannerIsRequired();
    }, 100);
  }

  public allowAll(): void {
    this.enableAnalytics();
    this.enableTargeting();
    this.enableGoogleTagManager();
    this.enableAppsFlyer();
    this.enableEtracker();
  }

  public disallowAll(): void {
    this.disableAnalytics();
    this.disableTargeting();
    this.disableGoogleTagManager();
    this.disableAppsFlyer();
    this.disableEtracker();
  }

  public checkIfBannerIsRequired(): void {
    this.isBannerRequired.next(
      !(
        ["true", "false"].indexOf(this.cookieService.get(DISABLE_ANALYTICS_COOKIES_COOKIE_KEY)) !== -1 &&
        ["true", "false"].indexOf(this.cookieService.get(DISABLE_TARGETING_COOKIES_COOKIE_KEY)) !== -1
      )
    );
  }

  public activeCookieBanner(): void {
    this.isBannerRequired.next(
      ["true", "false"].indexOf(this.cookieService.get(DISABLE_ANALYTICS_COOKIES_COOKIE_KEY)) !== -1 &&
        ["true", "false"].indexOf(this.cookieService.get(DISABLE_TARGETING_COOKIES_COOKIE_KEY)) !== -1
    );
  }

  public disableAnalytics(): void {
    this.cookieService.set(DISABLE_ANALYTICS_COOKIES_COOKIE_KEY, "true", {
      expires: DateHelper.getInstance().add(1, "year").toDate(),
      path: "/",
      secure: true,
      sameSite: "Strict"
    });

    this.removeAnalyticsCookies();
    this.removeTargetingCookies();

    this.onChanges();
  }

  public disableEtracker(): void {
    this.cookieService.set(DISABLE_ETRACKER_COOKIE_KEY, "true", {
      expires: DateHelper.getInstance().add(1, "year").toDate(),
      path: "/",
      secure: true,
      sameSite: "Strict"
    });

    this.etrackerConsent.setConsent(false);

    this.onChanges();
  }

  public disableAppsFlyer(): void {
    this.cookieService.set(DISABLE_APPSFLYER_COOKIE_KEY, "true", {
      expires: DateHelper.getInstance().add(1, "year").toDate(),
      path: "/",
      secure: true,
      sameSite: "Strict"
    });

    this.appsflyerConsent.setConsent(false);

    this.onChanges();
  }

  public disableTargeting(): void {
    this.cookieService.set(DISABLE_TARGETING_COOKIES_COOKIE_KEY, "true", {
      expires: DateHelper.getInstance().add(1, "year").toDate(),
      path: "/",
      secure: true,
      sameSite: "Strict"
    });

    this.removeTargetingCookies();

    this.onChanges();
  }

  public disableGoogleTagManager(): void {
    this.cookieService.set(DISABLE_GOOGLE_TAG_MANAGER_COOKIE_KEY, "true", {
      expires: DateHelper.getInstance().add(1, "year").toDate(),
      path: "/",
      secure: true,
      sameSite: "Strict"
    });

    this.removeTargetingCookies();

    this.onChanges();
  }

  public get isAnalyticsEnabled(): boolean {
    return this.cookieService.get(DISABLE_ANALYTICS_COOKIES_COOKIE_KEY) === "false";
  }

  public get isTargetingEnabled(): boolean {
    return this.cookieService.get(DISABLE_TARGETING_COOKIES_COOKIE_KEY) === "false";
  }

  public get isGoogleTagManagerEnabled(): boolean {
    return this.cookieService.get(DISABLE_GOOGLE_TAG_MANAGER_COOKIE_KEY) === "false";
  }

  public get isEtrackerEnabled(): boolean {
    return this.cookieService.get(DISABLE_ETRACKER_COOKIE_KEY) === "false";
  }

  public get isAppsFlyerEnabled(): boolean {
    return this.cookieService.get(DISABLE_APPSFLYER_COOKIE_KEY) === "false";
  }

  public enableAnalytics(): void {
    this.cookieService.set(DISABLE_ANALYTICS_COOKIES_COOKIE_KEY, "false", {
      expires: DateHelper.getInstance().add(1, "year").toDate(),
      path: "/",
      secure: true,
      sameSite: "Strict"
    });
    this.facebookConsent.setConsent(true);

    this.onChanges();
  }

  public enableEtracker(): void {
    this.cookieService.set(DISABLE_ETRACKER_COOKIE_KEY, "false", {
      expires: DateHelper.getInstance().add(1, "year").toDate(),
      path: "/",
      secure: true,
      sameSite: "Strict"
    });
    this.etrackerConsent.setConsent(true);

    this.onChanges();
  }

  public enableAppsFlyer(): void {
    this.cookieService.set(DISABLE_APPSFLYER_COOKIE_KEY, "false", {
      expires: DateHelper.getInstance().add(1, "year").toDate(),
      path: "/",
      secure: true,
      sameSite: "Strict"
    });
    this.appsflyerConsent.setConsent(true);

    this.onChanges();
  }

  public enableTargeting(): void {
    this.cookieService.set(DISABLE_TARGETING_COOKIES_COOKIE_KEY, "false", {
      expires: DateHelper.getInstance().add(1, "year").toDate(),
      path: "/",
      secure: true,
      sameSite: "Strict"
    });

    this.onChanges();
  }

  public enableGoogleTagManager(): void {
    this.cookieService.set(DISABLE_GOOGLE_TAG_MANAGER_COOKIE_KEY, "false", {
      expires: DateHelper.getInstance().add(1, "year").toDate(),
      path: "/",
      secure: true,
      sameSite: "Strict"
    });

    this.onChanges();
  }

  public removeAnalyticsCookies(): void {
    this.analyticsCookiesKeysList.forEach((key) => {
      this.cookieService.delete(key);
    });

    Object.keys(this.cookieService.getAll()).forEach((key) => {
      if (key.indexOf("_ga") === 0) {
        this.cookieService.delete(key);
      }
    });
  }

  public removeTargetingCookies(): void {
    this.targetingCookiesKeysList.forEach((key) => {
      this.cookieService.delete(key);
    });
  }

  private onChanges(): void {
    this.checkIfBannerIsRequired();
    this.changesEmitter.emit();
  }
}
